import styled, { useTheme } from 'styled-components';
import { useState } from 'react';
import { useClickAwayListener } from '@algoan/dashboard/utils';
import { Checkbox } from '@algoan/deep-dive/checkbox';
import { ArrowIcon } from '../icons';
import { addAlpha } from '@algoan/deep-dive/theme';

export interface FilterDropdownItem<T extends string> {
  display: string;
  value: T;
}

interface FilterDropdownProps<T extends string> {
  /** Label for the dropdown. This should describe the value of the input. */
  label: string;
  /** list of item for the dropdown */
  options: Array<FilterDropdownItem<T>>;
  onToggle: (option: T, isSelected: boolean) => void;
  selectedOptions: Array<FilterDropdownItem<T>>;
  /** itemRenderer allows to customize the list items. */
  itemRenderer?: (option: FilterDropdownItem<T>) => React.ReactNode;
  className?: string;
  ['data-testid']?: string;
}

export const FilterDropdown = <T extends string>({
  label,
  options,
  onToggle,
  selectedOptions,
  itemRenderer,
  className,
  'data-testid': dataTestId,
}: FilterDropdownProps<T>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = (): void => setIsOpen(!isOpen);
  const theme = useTheme();
  const dropdownContainerRef = useClickAwayListener<HTMLDivElement>(() => {
    setIsOpen(false);
  });

  return (
    <S.Container
      ref={dropdownContainerRef}
      className={className}
      data-testid={dataTestId ?? `filter-dropdown-${label}`}
      onClick={toggleDropdown}
      dropdownIsOpen={isOpen}
    >
      <S.ArrowContainer>
        <ArrowIcon direction={isOpen ? 'up' : 'down'} color={theme.secondary.softBlue1} />
      </S.ArrowContainer>
      <S.LabelContainer>
        {label}
        {selectedOptions.length > 0 && <S.NumberOfSelectedOptions>{selectedOptions.length}</S.NumberOfSelectedOptions>}
      </S.LabelContainer>
      {isOpen && (
        <S.DropdownList data-testid="list-options">
          {options.map((option: FilterDropdownItem<T>) => (
            <S.DropdownItem
              onClick={(event) => {
                event.stopPropagation();
                if (option) {
                  onToggle(option.value, !selectedOptions.includes(option));
                }
              }}
              isSelected={selectedOptions.includes(option)}
              role="option"
              key={option.value}
            >
              <Checkbox
                checked={selectedOptions.includes(option)}
                onChange={() => onToggle(option.value, !selectedOptions.includes(option))}
              />
              {itemRenderer ? itemRenderer(option) : option.display}
            </S.DropdownItem>
          ))}
        </S.DropdownList>
      )}
    </S.Container>
  );
};

const S = {
  Container: styled.div<{ dropdownIsOpen: boolean }>`
    position: relative;
    display: flex;
    height: 24px;
    border-radius: 23px;
    background: ${({ theme }) => theme.secondary.chalk};
    font-size: 12px;
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    color: ${({ theme }) => theme.secondary.softBlue4};
    cursor: pointer;
    ${({ dropdownIsOpen, theme }) =>
      dropdownIsOpen ? `box-shadow: 0px 2px 13px 0px ${addAlpha(theme.algoanBlue, 0.15)};` : ''}

    &:hover {
      box-shadow: 0px 2px 13px 0px ${({ theme }) => addAlpha(theme.algoanBlue, 0.15)};
    }
  `,
  ArrowContainer: styled.div`
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-right: 1px solid ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
    padding: 0px 8px;
  `,
  LabelContainer: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px 8px;
  `,
  DropdownList: styled.ul`
    position: absolute;
    display: inline-block;
    left: 0px;
    top: 28px;
    min-width: 100%;
    padding: 8px 0px;
    border-radius: 4px;
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    font-size: 12px;
    background-color: ${({ theme }) => theme.white};
    box-shadow: 0px 1px 15px 0px ${({ theme }) => addAlpha(theme.algoanBlue, 0.15)};
    z-index: 100;
  `,
  DropdownItem: styled.li<{ isSelected: boolean }>`
    height: 16px;
    display: flex;
    align-items: center;
    list-style: none;
    padding: 8px 12px;
    color: ${({ theme }) => theme.secondary.softBlue4};
    user-select: none;

    &:hover {
      background-color: ${({ theme }) => theme.backgroundGrey};
      cursor: ${({ isSelected }) => (isSelected ? '' : 'pointer')};
    }
  `,
  NumberOfSelectedOptions: styled.div`
    height: 18px;
    width: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-left: 4px;
    font-size: 10px;
    font-weight: ${({ theme }) => theme.fontWeight.semiBold};
    line-height: 25px;
    background-color: ${({ theme }) => theme.bluePeps};
    color: ${({ theme }) => theme.white};
  `,
};
